import { useGetTopicDetails, useInteractToNote } from '@/hooks/api/dashboard'
import { useAuth, useBrowserQuery } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import { NoteType, SIDE_PANEL_WIDTH, TopicType } from '@/utils/constants'
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material'
import React, { useMemo, useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { shallow } from 'zustand/shallow'
import { GradientContainer } from '@/pages/panels/__styled'

import { PatternNotesFooterPanel } from './molecules/PatternNotesFooterPanel'
import { Note, PatternDraft } from '@/types'
import PreviewDetailsPanel from './panels/DetailsPanel'
import { PreviewTopBar } from './molecules/PreviewTopBar'
import NoteLikes from '@/pages/panels/interactions/NoteLikes'
import NoteComments from '@/pages/panels/interactions/NoteComments'
import NoteShares from '@/pages/panels/interactions/NoteShares'
import { getFullImageURL, isBabylonType } from '@/utils/helper'
import { TopicDetailsContext } from '@/context/TopicDetails'
import colors from '@/styles/colors'
import { RBox } from '@/pages/__styled'
import PatternPreviewPage from '@/pages/pattern/PatternPreviewPage'
import PatternNotesInteractionView from '@/pages/panels/details/PatternNotesInteractionView'

export enum ViewStep {
  PREVIEW = '0',
  DETAILS = '1',
  COMMENTS = '2',
  LIKES = '3',
  SHARES = '4',
}

const PatternTopicPreview = () => {
  const [topicList] = useAppStore((state) => [state.home.topicList], shallow)
  const { user: ewUser } = useAuth()

  const query = useBrowserQuery()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const lessThanMedium = useMediaQuery(theme.breakpoints.down('lg'))

  const topicID = query.get('topicID') || ''
  const viewStep: ViewStep = (query.get('step') || '1') as ViewStep
  const interactToNote = useInteractToNote()
  const showDrawer: boolean = viewStep != ViewStep.PREVIEW
  const { topic, onClickBack } = useContext(TopicDetailsContext)
  const [showPatternNotesDrawer, setShowPatternNotesDrawer] = useState(false)

  const [isCommentsVisible, setCommentsVisible] = useState(false)

  const toggleComments = () => {
    setCommentsVisible((prev) => !prev)
  }

  const firstNote = useMemo(() => {
    const currentTopic = topicList[topicID]
    if (currentTopic) {
      if (currentTopic.firstNote && currentTopic?.notes?.length > 0) {
        const targetID = currentTopic.firstNote.noteID
        const note = topic.notes.find((e) => e.noteID == topic.firstNote.noteID)
        if (!note) {
          return currentTopic.firstNote
        }
        return note
      } else {
        return currentTopic.firstNote
      }
    }
  }, [topicID, topicList])

  const lastNote = useMemo(() => {
    const currentTopic = topicList[topicID]
    if (currentTopic) {
      if (currentTopic.latestNote && currentTopic?.notes?.length > 0) {
        const note = topic.notes.find((e) => e.noteID == topic.latestNote.noteID)
        return note
      } else {
        return currentTopic.latestNote
      }
    }
  }, [topicID, topicList])

  const previewImageURL = useMemo(() => {
    if (topic) {
      if (isBabylonType(topic.topicType)) {
        return firstNote?.egoList[0]?.photoAlbumURL
      } else if (topic.topicType == TopicType.ALBUM.toString()) {
        return lastNote?.egoList[0]?.photoAlbumURL
      }
    }
  }, [firstNote, lastNote, topic])

  const patternNoteGroups = useMemo(() => {
    let result = null
    if (topic && topic?.notes?.length > 0 && isBabylonType(topic.topicType)) {
      const draft: PatternDraft = { templateID: 1 }
      const noteMap = new Map<string, Note>()

      const sortedNotes = [...topic.notes].sort((a, b) => {
        const dateA = a.dateUpdated || a.dateCreated || ''
        const dateB = b.dateUpdated || b.dateCreated || ''
        return dateB.localeCompare(dateA)
      })

      sortedNotes.forEach((note: Note) => {
        if (note.noteType === '8') {
          // setup initial text overlay
        } else if (note.noteType === '9') {
          // preview
        }
        // else if (note.noteType !== '1'){ //&& note.noteText !== 'null' && note.noteText != null) {
        //   draft[note.noteName as keyof PatternDraft] = JSON.parse(note.noteText)
        // }

        if (!noteMap.has(note.noteName) && note.noteType !== '1') {
          noteMap.set(note.noteName, note)
          try {
            draft[note.noteName as keyof PatternDraft] = JSON.parse(note.noteText)
          } catch (e) {
            console.warn('Failed to parse note:', note.noteName)
          }
        }
      })

      // noteMap.forEach((note) => {
      //   try {
      //     draft[note.noteName as keyof PatternDraft] = JSON.parse(note.noteText);
      //   } catch (e) {
      //     console.warn('Failed to parse note for draft:', note.noteName);
      //   }
      // });

      // const byDateDesc = (a: Note, b: Note) => {
      //   const dateA = a.dateUpdated ? new Date(a.dateUpdated).getTime() : 0;
      //   const dateB = b.dateUpdated ? new Date(b.dateUpdated).getTime() : 0;
      //   return dateB - dateA;
      // };

      result = {}
      let templateNotes = sortedNotes.filter((e) => e.noteType == NoteType.TEMPLATE.toString())
      let materialNotes = sortedNotes.filter((e) => e.noteType == NoteType.MATERIAL.toString())
      let accentNotes = sortedNotes.filter((e) => e.noteType == NoteType.ACCENT.toString())
      let frameNotes = sortedNotes.filter((e) => e.noteType == NoteType.FRAME.toString())
      let printNotes = sortedNotes.filter((e) => e.noteType == NoteType.PRINT.toString())
      let colorNotes = sortedNotes.filter((e) => e.noteType == NoteType.COLOR.toString())
      let borderNotes = sortedNotes.filter((e) => e.noteType == NoteType.BOARDER.toString())
      let patternFrameNotes = sortedNotes.filter(
        (e) => e.noteType == NoteType.PATTERNFRAME.toString(),
      )

      result = {
        [NoteType.TEMPLATE]: templateNotes || [],
        [NoteType.MATERIAL]: materialNotes || [],
        [NoteType.ACCENT]: accentNotes || [],
        [NoteType.FRAME]: frameNotes || [],
        [NoteType.PRINT]: printNotes || [],
        [NoteType.COLOR]: colorNotes || [],
        [NoteType.BOARDER]: borderNotes || [],
        [NoteType.PATTERNFRAME]: patternFrameNotes || [],
        patternDetails: draft,
      }
    }
    return result
  }, [topic])

  const onClickEditTopic = () => {
    navigate(`/pattern?topicID=${topicID}`)
  }

  const onFooterCollapse = () => {
    query.delete('noteID')
    query.set('step', ViewStep.DETAILS)
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onClickComment = () => {
    // if (!showPatternNotesDrawer) {
    //   setShowPatternNotesDrawer(true);
    //   return;
    // }

    query.set('noteID', `${topic.firstNote.noteID}`)
    query.set('step', ViewStep.COMMENTS)
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }
  const onClickLikesCount = () => {
    query.set('noteID', `${topic.firstNote.noteID}`)
    query.set('step', ViewStep.LIKES)

    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onClickShares = () => {
    query.set('noteID', `${topic.firstNote.noteID}`)
    query.set('step', ViewStep.SHARES)

    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onClickExitPreview = () => {
    query.delete('noteID')
    query.set('step', ViewStep.DETAILS)
    navigate(`${pathname}?${query.toString()}`, { replace: true })
    onClickBack()
  }

  const onClickPreview = () => {
    query.delete('noteID')
    query.set('step', ViewStep.PREVIEW)
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }
  const onClickAlbumNote = (note: Note) => {
    query.set('noteID', `${note.noteID}`)
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onClickLike = () => {
    // Check if user is logged in before allowing like action
    if (!ewUser?.userID) {
      navigate('/auth?step=login')
      return
    }

    if (firstNote) {
      const isCurrentlyLiked = firstNote.isThumbsUp == 1
      const newInteractionValue = isCurrentlyLiked ? '0' : '1'

      interactToNote.mutateAsync({
        interactionType: '3',
        interaction: newInteractionValue,
        comment: 'test',
        noteID: firstNote.noteID,
        topicID,
      })
    }
  }

  const onClickMenuIcon = () => {
    setShowPatternNotesDrawer(true)
  }

  const dismissDrawer = () => {
    query.delete('noteID')
    query.delete('step')
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const renderDetailsPanel = () => (
    <GradientContainer sx={{ backgroundColor: colors.pageBackground }}>
      {viewStep == ViewStep.DETAILS ? (
        <PreviewDetailsPanel
          topic={topic}
          showPreviewImage={lessThanMedium}
          previewImageURL={previewImageURL}
          firstNote={firstNote}
          onClickBack={onClickBack}
          onDeletedTopic={() => {}}
          onShowInteractions={() => {}}
          onClickLike={onClickLike}
          onClickComment={onClickComment}
          onClickPreview={onClickPreview}
          onClickLikesCount={onClickLikesCount}
          onClickAlbumNote={onClickAlbumNote}
          onClickEditTopic={onClickEditTopic}
        />
      ) : viewStep == ViewStep.LIKES ? (
        <NoteLikes trackKey='topicID' />
      ) : (
        <NoteComments trackKey='topicID' />
      )}
    </GradientContainer>
  )

  return topic.topicType == TopicType.PATTERN.toString() ||
    topic.topicType == TopicType.GALLERY.toString() ||
    topic.topicType == TopicType.QUILT.toString() ||
    topic.topicType == TopicType.CUSTOM_PRINTS.toString() ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Left Section: Main Content */}
      <Box
        sx={{
          flex: showPatternNotesDrawer ? 2 : 1, // Adjust flex based on whether the right panel is shown
          transition: 'flex 0.3s ease', // Smooth resize animation
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* TopBar */}
        <PreviewTopBar
          topic={topic}
          showingDetailsInPanel={false}
          showTopicInfo={true}
          onClickMenuIcon={onClickMenuIcon}
          onClickBack={lessThanMedium ? onClickExitPreview : onClickBack}
          onClickExitPreview={onClickExitPreview}
          onClickEdit={onClickEditTopic}
          isLiked={firstNote?.isThumbsUp == 1}
          onClickLike={onClickLike}
          onClickComment={onClickComment}
          onClickPreview={onClickPreview}
          onClickLikesCount={onClickLikesCount}
          onClickShares={onClickShares}
        />

        {/* Preview Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <PatternPreviewPage topicID={topic?.topicID} />
        </Box>

        {/* Footer */}
        <PatternNotesFooterPanel
          patternNoteGroups={patternNoteGroups}
          onCollapse={onFooterCollapse}
        />
      </Box>

      {/* Right Section: PatternNotesInteractionView */}

      {showDrawer &&
        (viewStep === ViewStep.COMMENTS ||
          viewStep === ViewStep.LIKES ||
          viewStep === ViewStep.SHARES) && (
          <Drawer
            sx={{
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                minWidth: SIDE_PANEL_WIDTH,
                height: '100hv',
                borderWidth: 0,
              },
            }}
            variant='temporary'
            anchor='right'
            open={showDrawer}
            onClose={dismissDrawer}
          >
            <Box
              sx={{
                // flex:
                //   viewStep === ViewStep.COMMENTS ||
                //   viewStep === ViewStep.LIKES ||
                //   viewStep === ViewStep.SHARES
                //     ? '0 0 500px'
                //     : '0 0 0',
                minWidth: '300px', // Minimum width
                maxWidth: '500px', // Maximum width
                transition: 'flex 0.3s ease', // Smooth resize animation
                backgroundColor: colors.pageBackground,
                boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.1)',
                height: '100%',
                flexDirection: 'column',

                zIndex: 10,
                display:
                  viewStep === ViewStep.COMMENTS ||
                  viewStep === ViewStep.LIKES ||
                  viewStep === ViewStep.SHARES
                    ? 'flex'
                    : 'none',
              }}
            >
              {viewStep === ViewStep.COMMENTS ? (
                <NoteComments trackKey='topicID' />
              ) : viewStep === ViewStep.LIKES ? (
                <NoteLikes trackKey='topicID' />
              ) : viewStep === ViewStep.SHARES ? (
                <NoteShares trackKey='topicID' />
              ) : null}
            </Box>
          </Drawer>
        )}
    </Box>
  ) : (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        sx={{
          display: 'flex',
          height: '100%',
          width: lessThanMedium ? '100%' : showDrawer ? `calc(100% - ${SIDE_PANEL_WIDTH})` : '100%',
        }}
      >
        {(!lessThanMedium || (lessThanMedium && viewStep != ViewStep.DETAILS)) && (
          <PreviewTopBar
            topic={topic}
            onClickBack={lessThanMedium ? onClickExitPreview : onClickBack}
            showingDetailsInPanel={viewStep == ViewStep.DETAILS}
            showTopicInfo={!showDrawer}
            isLiked={firstNote?.isThumbsUp == 1}
            onClickMenuIcon={onClickMenuIcon}
            onClickEdit={onClickEditTopic}
            onClickExitPreview={onClickExitPreview}
            onClickLike={onClickLike}
            onClickComment={onClickComment}
            onClickPreview={onClickPreview}
            onClickLikesCount={onClickLikesCount}
            onClickShares={() => {}}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <>
            {(!lessThanMedium || (lessThanMedium && viewStep != ViewStep.DETAILS)) &&
              previewImageURL && (
                <Box
                  borderRadius={'8px'}
                  sx={{
                    aspectRatio: 1,
                    width: lessThanMedium ? '70%' : '35%',
                    backgroundImage: `url(${getFullImageURL(previewImageURL)})`,
                    backgroundSize: 'cover',
                    '&::before': {
                      content: '""',
                      height: '150px',
                      backgroundImage:
                        'linear-gradient(180deg, rgba(22, 23, 25, 1) 0%, rgba(29, 30, 33, 1) 100%)',
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                ></Box>
              )}
          </>

          {showDrawer && !lessThanMedium && (
            <Drawer
              sx={{
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: SIDE_PANEL_WIDTH,
                  height: '100%',
                  borderWidth: 0,
                },
              }}
              variant='permanent'
              anchor='right'
              open={showDrawer}
            >
              {renderDetailsPanel()}
            </Drawer>
          )}

          {viewStep == ViewStep.DETAILS && lessThanMedium && (
            <Drawer
              sx={{
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: '100%',
                  height: '100%',
                  borderWidth: 0,
                },
              }}
              variant='permanent'
              open={true}
            >
              {renderDetailsPanel()}
            </Drawer>
          )}

          {viewStep != ViewStep.DETAILS && viewStep != ViewStep.PREVIEW && lessThanMedium && (
            <Drawer
              sx={{
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: '100%',
                  height: '100%',
                  borderWidth: 0,
                },
              }}
              variant='permanent'
              anchor='right'
              open={showDrawer}
            >
              {renderDetailsPanel()}
            </Drawer>
          )}
        </Box>
        {!lessThanMedium && viewStep != ViewStep.DETAILS && isBabylonType(topic.topicType) && (
          <PatternNotesFooterPanel
            patternNoteGroups={patternNoteGroups}
            onCollapse={onFooterCollapse}
          />
        )}
      </Box>

      {lessThanMedium && (
        <Drawer
          sx={{
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: SIDE_PANEL_WIDTH,
              height: '100%',
              borderWidth: 0,
              backgroundColor: colors.pageBackground,
            },
          }}
          variant='temporary'
          anchor='left'
          open={showPatternNotesDrawer}
          onClose={() => setShowPatternNotesDrawer(false)}
        >
          <PreviewDetailsPanel
            topic={topic}
            showPreviewImage={false}
            previewImageURL={previewImageURL}
            firstNote={firstNote}
            showNotesOnly={true}
            onClickBack={() => setShowPatternNotesDrawer(false)}
            onDeletedTopic={() => {}}
            onShowInteractions={() => {}}
            onClickLike={onClickLike}
            onClickComment={onClickComment}
            onClickPreview={onClickPreview}
            onClickLikesCount={onClickLikesCount}
            onClickAlbumNote={onClickAlbumNote}
            onClickEditTopic={onClickEditTopic}
          />
        </Drawer>
      )}
    </>
  )
}

export default PatternTopicPreview
